import AddIcon from "@mui/icons-material/Add";
import CachedIcon from "@mui/icons-material/Cached";
import DownloadIcon from "@mui/icons-material/Download";
import {
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import {
  MRT_ColumnDef,
  MRT_TableInstance,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import getAxiosError from "../../app/api/axiosError";
import { updateUserTokenToInvalid } from "../../app/features/login/loginSlice";
import {
  downloadParcelCenterDataToCsv,
  downloadParcelCenterDataToXlsx,
  getparcelCenterList,
} from "../../app/features/parcelCenter/parcelCenterSlice";
import { RootState, useAppDispatch } from "../../app/features/store/store";
import { ParcelCenterResponse } from "../../app/models/responseModels/parcelCenterResponse";
import Breadcrumb from "../../components/common/breadcrumb/Breadcrumb";
import {
  useSubmitModal,
  useSubmitModalWithParameter,
} from "../../components/common/modalPopup/useSubmitModal";
import ModalWithMuiTabs from "../../components/common/tabs/ModalWithMuiTabs";
import { CustomTooltip } from "../../components/common/tooltip/CustomToolTip";
import { PAGES } from "../../enums/PagesEnum";
import { downloadCsv, downloadXlsx } from "../../helpers/downloadFile";
import { getDefaultMRTOptions } from "../dedicatedDelivery/DefaultMrtOptions";
import AddParcelCenterModal from "./forms/AddParcelCenterModal";
import EditParcelCenter from "./forms/EditParcelCenter";
import {
  PARCEL_CENTER_COLUMN_NAME,
  PARCEL_CENTER_LABEL_NAME,
  parcelCenterFilterColumns,
} from "./ParcelCenterConstant";
import AssociatedZipcodeGrid from "./associated zipcode/AssociatedZipcodeGrid";
import GridDataFilter, {
  FilterableColumns,
  FilterableColumnsState,
} from "../../components/common/filter/GridDataFilter";

const breadcrumb = ["Parcel Centers"];

const ParcelCenters = () => {
  const defaultMRTOptions = getDefaultMRTOptions<ParcelCenterResponse>();

  // flag to open/close route per store modal
  const [openActionsModal, setOpenActionsModal] = useState<boolean>(false);

  const dispatch = useAppDispatch();

  const { isLoading, parcelCenterList } = useSelector(
    (state: RootState) => state.parcelCenter
  );

  // ***** Get page Permissions *****
  const pagePermission = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings?.pagePermissions.find(
      (x) => x.pageName === PAGES.PARCEL_CENTERS
    )
  );

  const IsAdmin = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings
      ? state.login?.authResponse?.userSettings?.roles.includes(
          "Administrators"
        )
      : false
  );

  const IsCreatePermission = pagePermission
    ? pagePermission.isCreatePermission
    : false;

  // ***** States *****
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [resetFilter, setResetFilter] = useState<boolean>(false);

  const [clonedParcelCenterData, setClonedParcelCenterData] =
    useState<ParcelCenterResponse[]>(parcelCenterList);

  // ***** Callbacks *****
  const getParcelCenterData = useCallback(async () => {
    try {
      const response = await dispatch(getparcelCenterList()).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message!, {
          toastId: "parcelCenterList",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "parcelCenterListException",
      });
    }
  }, [dispatch]);

  // ***** UseEffects *****
  useEffect(() => {
    let isFunctionRan = true;
    if (isFunctionRan) {
      getParcelCenterData();
    }

    return () => {
      isFunctionRan = false;
    };
  }, [getParcelCenterData]);

  useEffect(() => {
    setClonedParcelCenterData(parcelCenterList);
  }, [parcelCenterList]);

  // ***** UseMemos *****
  // Columns
  const columns = useMemo<MRT_ColumnDef<ParcelCenterResponse>[]>(
    () => [
      {
        accessorKey: PARCEL_CENTER_COLUMN_NAME.PARCEL_CENTER_NAME,
        header: PARCEL_CENTER_LABEL_NAME.PARCEL_CENTER_NAME,
        size: 100,
      },
      {
        accessorKey: PARCEL_CENTER_COLUMN_NAME.ADDRESS,
        header: PARCEL_CENTER_LABEL_NAME.ADDRESS,
        size: 100,
      },
      {
        accessorKey: PARCEL_CENTER_COLUMN_NAME.CITY,
        header: PARCEL_CENTER_LABEL_NAME.CITY,
        size: 100,
      },
      {
        accessorKey: PARCEL_CENTER_COLUMN_NAME.STATE,
        header: PARCEL_CENTER_LABEL_NAME.STATE,
        size: 100,
      },
      {
        accessorKey: PARCEL_CENTER_COLUMN_NAME.ZIPCODE,
        header: PARCEL_CENTER_LABEL_NAME.ZIPCODE,
        size: 100,
      },
      {
        accessorKey: PARCEL_CENTER_COLUMN_NAME.TIME_ZONE,
        header: PARCEL_CENTER_LABEL_NAME.TIME_ZONE,
        size: 100,
      },
      {
        accessorKey: PARCEL_CENTER_COLUMN_NAME.LATITUDE,
        header: PARCEL_CENTER_LABEL_NAME.LATITUDE,
        size: 100,
      },
      {
        accessorKey: PARCEL_CENTER_COLUMN_NAME.LONGITUDE,
        header: PARCEL_CENTER_LABEL_NAME.LONGITUDE,
        size: 100,
      },
      {
        accessorKey: PARCEL_CENTER_COLUMN_NAME.CREATED_ON,
        header: PARCEL_CENTER_LABEL_NAME.CREATED_ON,
        size: 80,
        Cell: ({ cell, row }) => (
          <div>
            {row.original.createdOn ? (
              <span>
                {new Date(cell.getValue<string>()).toLocaleString("en-US", {
                  hourCycle: "h12",
                })}
              </span>
            ) : (
              <span></span>
            )}
          </div>
        ),
      },
      {
        accessorKey: PARCEL_CENTER_COLUMN_NAME.UPDATED_ON,
        header: PARCEL_CENTER_LABEL_NAME.UPDATED_ON,
        size: 200,
        Cell: ({ cell, row }) => (
          <div>
            {row.original.updatedOn ? (
              <span>
                {new Date(cell.getValue<string>()).toLocaleString("en-US", {
                  hourCycle: "h12",
                })}
              </span>
            ) : (
              <span></span>
            )}
          </div>
        ),
      },
      {
        header: "Associated FDP(s)",
        size: 200,
        Cell: ({ cell, row }) =>
          row.original.fdps.map((e) => e.companyName).join(", "),
      },
    ],
    [parcelCenterList]
  );

  // ***** Handlers *****
  const handleRefresh = () => {
    getParcelCenterData();
  };

  const handleDownloadMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCsvFileDownload = async (
    event: React.MouseEvent<HTMLElement>
  ) => {
    handleClose();
    event.preventDefault();
    try {
      const response = await dispatch(
        downloadParcelCenterDataToCsv(parcelCenterList)
      ).unwrap();

      if (response.isSuccess) {
        downloadCsv(response.data);
      } else {
        toast.error(response.message!, {
          toastId: "parcelCenterDownloadCsv",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "parcelCenterDownloadCsvException",
      });
    }
  };

  const handleXlsxFileDownload = async (
    event: React.MouseEvent<HTMLElement>
  ) => {
    handleClose();
    event.preventDefault();
    try {
      const response = await dispatch(
        downloadParcelCenterDataToXlsx(parcelCenterList)
      ).unwrap();

      if (response) {
        downloadXlsx(response, "ParcelCenter.xlsx");
      } else {
        toast.error("unable to download file", {
          toastId: "parcelCenterDownloadXlsx",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "parcelCenterDownloadXlsxException",
      });
    }
  };

  const tabs = (pcData: ParcelCenterResponse) => {
    return [
      {
        tabName: "General",
        tabContent: (
          <EditParcelCenter
            parcelCenterData={pcData}
            onCompleted={handleRefresh}
          />
        ),
        index: 0,
      },
      {
        tabName: "ZipCode(s)",
        tabContent: <AssociatedZipcodeGrid data={pcData.zipcodes} />,
        index: 1,
      },
      {
        tabName: "History",
        tabContent: <div>Content for Tab 2</div>,
        index: 2,
      },
    ];
  };

  // ***** Modal Popups *****
  const [editParcelCenterModal, openEditParcelCenterModal] =
    useSubmitModalWithParameter<ParcelCenterResponse>(
      (parcelCenterRowData, closeEditParcelCenterModal) => (
        <ModalWithMuiTabs
          isOpen={openActionsModal}
          handleClose={() => {
            setOpenActionsModal(false);
          }}
          tabDetails={tabs(parcelCenterRowData)}
        />
      )
    );

  const [addParcelCenterModal, openAddParcelCenterModal] = useSubmitModal(
    (closeAddParcelCenterModal) => (
      <AddParcelCenterModal
        closeAddParcelCenterModal={closeAddParcelCenterModal}
        onCompleted={getParcelCenterData}
      />
    )
  );

  const table = useMaterialReactTable({
    ...defaultMRTOptions,
    columns: columns,
    data: clonedParcelCenterData,
    initialState: {
      ...defaultMRTOptions.initialState,
      columnVisibility: {
        timezone: false,
        createdOn: false,
        updatedOn: false,
        latitude: false,
        longitude: false,
      },
      sorting: [{ desc: false, id: "id" }],
    },
    state: {
      showProgressBars: isLoading,
      sorting: [{ desc: false, id: "id" }],
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (e) => {
        openEditParcelCenterModal(row.original);
        setOpenActionsModal(true);
      },
      ...defaultMRTOptions.muiTableBodyRowProps,
      sx: {
        cursor: "pointer",
      },
    }),
    renderTopToolbarCustomActions: ({ table }) =>
      renderTopToolbarCustomActions(table),
    renderBottomToolbarCustomActions: () => {
      return (
        <Typography variant="subtitle2" component="p">
          Total Rows: {clonedParcelCenterData.length}
        </Typography>
      );
    },
  });

  // ***** Datagrid Custom Actions *****
  const renderTopToolbarCustomActions = (
    table: MRT_TableInstance<ParcelCenterResponse>
  ) => {
    return (
      <Box display="flex" alignContent="center">
        <CustomTooltip arrow title="Refresh">
          <IconButton onClick={handleRefresh}>
            <CachedIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </CustomTooltip>
        <CustomTooltip arrow title="Download/Export">
          <IconButton type="button" onClick={handleDownloadMenuClick}>
            <DownloadIcon sx={{ color: "blue" }} />
          </IconButton>
        </CustomTooltip>
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "basic-button",
          }}
        >
          <MenuItem onClick={(event) => handleCsvFileDownload(event)}>
            Csv
          </MenuItem>
          <MenuItem onClick={(event) => handleXlsxFileDownload(event)}>
            Xlsx
          </MenuItem>
        </Menu>
        {IsCreatePermission && IsAdmin && (
          <CustomTooltip
            arrow
            title="Add New"
            onClick={() => {
              openAddParcelCenterModal();
            }}
          >
            <span>
              <IconButton type="button" disabled={isLoading}>
                <AddIcon sx={{ color: "primary.main" }} />
              </IconButton>
            </span>
          </CustomTooltip>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Breadcrumb pageNameList={breadcrumb} />
      <>
        <GridDataFilter
          filterableColumns={parcelCenterFilterColumns}
          title="Search Parcel Center Data"
          dateRange={undefined}
          resetFlag={resetFilter}
          asyncSubmitFunction={(filterState: FilterableColumnsState) => {
            if (Object.keys(filterState).length) {
              const filteredData = parcelCenterList.filter(
                (client: Record<string, any>) => {
                  for (const key in filterState) {
                    if (filterState.hasOwnProperty(key)) {
                      const filterValues = filterState[key].values;
                      if (key === "clientCreatedOn") {
                        if (filterValues.length === 2) {
                          const clientCreatedDate = new Date(client[key]);
                          const startDate = new Date(filterValues[0]);
                          const endDate = new Date(filterValues[1]);

                          endDate.setHours(23, 59, 59, 999);

                          const isDateInRange =
                            clientCreatedDate >= startDate &&
                            clientCreatedDate <= endDate;
                          if (!isDateInRange) {
                            return false;
                          }
                        }
                      } else if (key === "fdps") {
                        const fdps = client[key];
                        const fdpsMatch = fdps.some((e: Record<string, any>) =>
                          filterValues.includes(e.companyName)
                        );
                        if (!fdpsMatch) {
                          return false;
                        }
                      } else {
                        if (
                          filterValues.length > 0 &&
                          !filterValues.includes(client[key])
                        ) {
                          return false;
                        }
                      }
                    }
                  }
                  return true;
                }
              );
              console.log(filterState, parcelCenterList, filteredData);
              setClonedParcelCenterData(filteredData);
            } else {
              setClonedParcelCenterData(parcelCenterList);
            }
          }}
          asyncSelectedColumnValues={(
            columnName: string,
            setClFilterableColumns: React.Dispatch<
              React.SetStateAction<FilterableColumns>
            >
          ) => {
            let filteredData: ParcelCenterResponse[];
            if (columnName !== "fdps") {
              filteredData = parcelCenterList
                .map((e: Record<string, any>) => e[columnName])
                .filter((value) => value !== undefined && value !== null);
            } else {
              filteredData = parcelCenterList
                .flatMap((e) => e[columnName])
                .map((e: Record<string, any>) => e.companyName)
                .filter((value) => value !== undefined && value !== null);
            }
            setClFilterableColumns((prevState: any) => {
              prevState[columnName].options =
                Array.from(new Set(filteredData)) ?? [];
              return prevState;
            });
          }}
        />
        <Divider />
        <br />
      </>
      <MaterialReactTable table={table} />
      {addParcelCenterModal}
      {editParcelCenterModal}
    </Box>
  );
};

export default ParcelCenters;
